import React, { useState, useEffect } from 'react';

import { Button } from '@depop/web-ui-kit/Button';
import { InputV2 } from '@depop/web-ui-kit/InputV2';

import styles from './styles.module.css';

import { getCurrentOrigin } from '@/modules/routing/helpers';
import { useClipboard } from '@/utils/useClipboard';

export function MagicLinkPaste() {
  const { readFromClipboard } = useClipboard({
    componentName: 'MagicLinkPaste',
  });
  const [magicLinkInput, setMagicLinkInput] = useState('');
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [transformedUrl, setTransformedUrl] = useState<string | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);

  function validateMagicLink(url: string): boolean {
    const magicLinkPattern =
      /\/(?:login|signup|email-change)\/magic-link\/verify\/[^\/]+/;
    return magicLinkPattern.test(url);
  }

  function transformUrl(inputUrl: string): string | null {
    try {
      const parsedUrl = new URL(inputUrl);
      const currentOrigin = getCurrentOrigin();
      return `${currentOrigin}${parsedUrl.pathname}${parsedUrl.search}`;
    } catch {
      return null;
    }
  }

  async function handlePasteFromClipboard(): Promise<void> {
    const text = await readFromClipboard();
    if (text) {
      setMagicLinkInput(text);
    }
  }

  function handleNavigate(): void {
    if (transformedUrl) {
      setIsNavigating(true);
      window.location.href = transformedUrl;
    }
  }

  useEffect(() => {
    if (!magicLinkInput) {
      setIsValid(null);
      setTransformedUrl(null);
      return;
    }

    const valid = validateMagicLink(magicLinkInput);
    setIsValid(valid);

    if (valid) {
      const transformed = transformUrl(magicLinkInput);
      setTransformedUrl(transformed);
    } else {
      setTransformedUrl(null);
    }
  }, [magicLinkInput]);

  useEffect(() => {
    if (transformedUrl && isValid) {
      handleNavigate();
    }
  }, [transformedUrl, isValid]);

  return (
    <div>
      <div className={styles.inputWrapper}>
        <InputV2
          id="magic-link-paste"
          labelText="Magic Link URL"
          type="text"
          value={magicLinkInput}
          onChange={(e) => setMagicLinkInput(e.target.value)}
          onClear={() => {
            setMagicLinkInput('');
            setIsValid(null);
          }}
          errorText={isValid === false ? 'Invalid magic link' : undefined}
          disabled={isNavigating}
        />
      </div>
      {!transformedUrl && (
        <Button
          type="button"
          onClick={handlePasteFromClipboard}
          disabled={isNavigating}
          aria-label="Paste from clipboard"
          block
        >
          Paste
        </Button>
      )}
      {transformedUrl && isValid && (
        <Button
          onClick={handleNavigate}
          isLoading={isNavigating}
          disabled={isNavigating}
          block
        >
          Navigate
        </Button>
      )}
    </div>
  );
}
